import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import { Button, Box } from "@lib/components";
import moment, { Moment } from "moment-timezone";
import { withTranslation, WithTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import styled from "styled-components";
import { dineInTableCapacity } from "@lib/common";
import { UI } from "../../../../../core/ui";

const DEFAULT_TIMELINE_START = 9;
const DEFAULT_TIMELINE_END = 23;
/** Default block width when booking has no stored duration (matches cloudtable prototype turn default). */
const DEFAULT_BLOCK_DURATION_MINS = 90;

/**
 * Cloudtable-style floor grid: rows need ~80–100px so the table id + seat line can breathe.
 * (Our previous floor was ~56px because laneHeight 44 × 1 + 8 clamped to 56.)
 */
const MIN_TABLE_ROW_PX = 80;
/** Vertical space per stacked booking lane (booking chip height ≈ this − 4). */
const BOOKING_LANE_HEIGHT_PX = 52;
/** Table column width — must match `CornerCell` / `RowLabel` and `tableColW` in render. */
const TABLE_COL_WIDTH_NARROW_PX = 110;
const TABLE_COL_WIDTH_WIDE_PX = 132;

/** Cloudtable-inspired page shell: queue + bento grid. */
const CalendarShell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	@media (min-width: 1024px) {
		flex-direction: row;
		align-items: stretch;
		min-height: 440px;
	}
`;

const CalendarPageHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	margin-bottom: 0.25rem;
	@media (min-width: 768px) {
		flex-direction: row;
		align-items: flex-end;
		justify-content: space-between;
		gap: 1rem;
	}
`;

const CalendarTitle = styled.h2`
	margin: 0;
	font-size: 1.75rem;
	font-weight: 800;
	color: #0f172a;
	line-height: 1.15;
	letter-spacing: -0.02em;
`;

const CalendarSubtitle = styled.p`
	margin: 0;
	font-size: 0.8125rem;
	font-weight: 600;
	color: #64748b;
	text-transform: uppercase;
	letter-spacing: 0.12em;
`;

const CalendarToolbar = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.65rem;
`;

const ToolbarPill = styled.div`
	display: inline-flex;
	align-items: center;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
	overflow: hidden;
	height: 44px;
`;

const ToolbarIconBtn = styled.button`
	border: none;
	background: transparent;
	padding: 0 0.65rem;
	height: 100%;
	cursor: pointer;
	color: #475569;
	font-size: 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s ease;
	&:hover {
		background: #f8fafc;
	}
`;

const ToolbarTodayBtn = styled.button`
	border: none;
	border-left: 1px solid #e2e8f0;
	border-right: 1px solid #e2e8f0;
	background: transparent;
	padding: 0 0.85rem;
	height: 100%;
	cursor: pointer;
	font-size: 0.65rem;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: #0f172a;
	&:hover {
		background: #f8fafc;
	}
`;

const TurnPill = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	padding: 0 0.85rem;
	height: 44px;
	box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
`;

const TurnPillLabel = styled.span`
	font-size: 0.625rem;
	font-weight: 800;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.14em;
	white-space: nowrap;
`;

const TurnPillInput = styled.input`
	width: 2.75rem;
	border: none;
	background: transparent;
	font-size: 0.875rem;
	font-weight: 700;
	color: #0f172a;
	text-align: center;
	padding: 0;
	&:focus {
		outline: none;
	}
`;

const QueuePanel = styled(Box)`
	width: 100%;
	flex-shrink: 0;
	border-radius: 1.5rem;
	border: 1px solid #e2e8f0;
	box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
	background: #fff;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	@media (min-width: 1024px) {
		width: 288px;
		max-width: 100%;
	}
`;

const QueueHeader = styled.div`
	padding: 1.25rem 1.5rem;
	border-bottom: 1px solid #f1f5f9;
	background: linear-gradient(180deg, #fafbfc 0%, #f8fafc 100%);
	font-size: 0.6875rem;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.18em;
	color: #0f172a;
`;

const QueueList = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 1rem;
	max-height: 380px;
`;

/** Cloudtable-style queue card (unallocated list). */
const QueueCardRoot = styled.div<{
	$active: boolean;
	$unconfirmed: boolean;
}>`
	width: 100%;
	border-radius: 1rem;
	padding: 1rem;
	margin-bottom: 0.75rem;
	cursor: pointer;
	border: 2px solid
		${(p) =>
			p.$active
				? "#6366f1"
				: p.$unconfirmed
					? "#fda4af"
					: "#f1f5f9"};
	background: ${(p) =>
		p.$active
			? "rgba(99, 102, 241, 0.06)"
			: p.$unconfirmed
				? "#fff"
				: "#fff"};
	box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
	transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease,
		transform 0.15s ease;
	${(p) =>
		p.$active
			? `box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);`
			: ""}
	&:hover {
		border-color: ${(p) => (p.$unconfirmed ? "#fb7185" : "#a5b4fc")};
		box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
	}
`;

const QueueCardTop = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 0.5rem;
`;

const QueueGuest = styled.span<{ $unconfirmed: boolean }>`
	font-size: 0.875rem;
	font-weight: 800;
	color: ${(p) => (p.$unconfirmed ? "#be123c" : "#0f172a")};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

const QueueTime = styled.span`
	font-size: 10px;
	font-weight: 700;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.02em;
`;

const QueueParty = styled.span`
	flex-shrink: 0;
	font-size: 10px;
	font-weight: 800;
	padding: 0.15rem 0.5rem;
	border-radius: 8px;
	background: #0f172a;
	color: #fff;
	text-transform: uppercase;
`;

const QueueBadge = styled.span<{ $variant: "assign" | "ready" }>`
	font-size: 9px;
	font-weight: 800;
	text-transform: uppercase;
	border-radius: 6px;
	padding: 0.2rem 0.45rem;
	${(p) =>
		p.$variant === "assign"
			? "background: #ffe4e6; color: #be123c;"
			: "background: #d1fae5; color: #047857;"}
`;

const QueuePlaceHint = styled.span`
	font-size: 9px;
	font-weight: 800;
	color: #4f46e5;
	text-transform: uppercase;
	letter-spacing: 0.12em;
`;

const QueueFooter = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 0.5rem;
`;

const QueueConfirmActions = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 0.5rem;
	margin-top: 0.5rem;
`;

const QueueConfirmBtn = styled.button<{ $primary?: boolean }>`
	border: none;
	border-radius: 12px;
	padding: 0.55rem 0.75rem;
	font-size: 9px;
	font-weight: 800;
	text-transform: uppercase;
	cursor: pointer;
	color: #fff;
	background: ${(p) => (p.$primary ? "#2563eb" : "#0f172a")};
	box-shadow: ${(p) => (p.$primary ? "0 4px 14px rgba(37, 99, 235, 0.25)" : "none")};
	&:hover {
		filter: brightness(1.06);
	}
`;

const GridWrap = styled(Box)<{ $placing?: boolean }>`
	flex: 1;
	min-width: 0;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	border-radius: 1.5rem;
	border: 1px solid #e2e8f0;
	box-shadow: 0 10px 40px -12px rgba(15, 23, 42, 0.15);
	background: #fff;
	${(p) =>
		p.$placing
			? `
		box-shadow: 0 0 0 4px rgba(129, 140, 248, 0.22), 0 10px 40px -12px rgba(15, 23, 42, 0.15);
		border-color: #c7d2fe !important;
	`
			: ""}
`;

const ScrollArea = styled.div`
	overflow: auto;
	flex: 1;
	position: relative;
	-webkit-overflow-scrolling: touch;
`;

const StickyHeader = styled.div`
	display: flex;
	position: sticky;
	top: 0;
	z-index: 5;
	background: rgba(255, 255, 255, 0.92);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	border-bottom: 2px solid #f1f5f9;
	box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04);
`;

const CornerCell = styled.div`
	width: ${TABLE_COL_WIDTH_NARROW_PX}px;
	flex-shrink: 0;
	padding: 0.95rem 0.5rem;
	text-align: center;
	border-right: 2px solid #f1f5f9;
	background: #f8fafc;
	font-size: 0.6875rem;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.12em;
	color: #64748b;
	@media (min-width: 768px) {
		width: ${TABLE_COL_WIDTH_WIDE_PX}px;
	}
`;

const HourTrack = styled.div`
	display: flex;
	flex-shrink: 0;
`;

const HourCell = styled.div<{ $w: number }>`
	width: ${(p) => p.$w}px;
	flex-shrink: 0;
	padding: 0.85rem 0.4rem;
	border-left: 1px solid #f1f5f9;
	font-size: 0.75rem;
	font-weight: 800;
	color: #475569;
	white-space: nowrap;
`;

const GridRow = styled.div`
	display: flex;
	border-bottom: 1px solid #f1f5f9;
	transition: opacity 0.2s ease, filter 0.2s ease;
`;

const RowLabel = styled.div`
	width: ${TABLE_COL_WIDTH_NARROW_PX}px;
	flex-shrink: 0;
	padding: 0.85rem 0.45rem;
	border-right: 2px solid #f1f5f9;
	background: #fff;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	position: sticky;
	left: 0;
	z-index: 3;
	box-shadow: 4px 0 12px -4px rgba(15, 23, 42, 0.08);
	@media (min-width: 768px) {
		width: ${TABLE_COL_WIDTH_WIDE_PX}px;
	}
`;

const RowLabelTitle = styled.span`
	font-size: 1.3125rem;
	font-weight: 800;
	color: #0f172a;
	letter-spacing: -0.03em;
	line-height: 1.1;
`;

const RowLabelMeta = styled.span`
	font-size: 0.6875rem;
	font-weight: 700;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	margin-top: 0.35rem;
`;

const TimelineTrack = styled.div<{ $w: number; $slot: number; $minor: number }>`
	position: relative;
	min-height: ${MIN_TABLE_ROW_PX}px;
	width: ${(p) => p.$w}px;
	flex-shrink: 0;
	background-image: linear-gradient(
			to right,
			#f1f5f9 1px,
			transparent 1px
		),
		linear-gradient(to right, #e2e8f0 1px, transparent 1px);
	background-size: ${(p) => p.$minor}px 100%, ${(p) => p.$slot}px 100%;
`;

const NowLine = styled.div<{ $left: number }>`
	position: absolute;
	top: 0;
	bottom: 0;
	left: ${(p) => p.$left}px;
	width: 2px;
	background: #e53935;
	z-index: 4;
	pointer-events: none;
	opacity: 0.85;
	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: -4px;
		width: 10px;
		height: 10px;
		border-radius: 50%;
		background: #e53935;
	}
`;

const BookingBlockOuter = styled.div<{
	$left: number;
	$width: number;
	$top: number;
	$laneHeight: number;
	$z: number;
	$pe?: string;
}>`
	position: absolute;
	left: ${(p) => p.$left}px;
	width: ${(p) => Math.max(p.$width, 48)}px;
	top: ${(p) => p.$top}px;
	height: ${(p) => p.$laneHeight - 4}px;
	min-height: 40px;
	z-index: ${(p) => p.$z};
	pointer-events: ${(p) => p.$pe || "auto"};
`;

const BookingBlockBtn = styled.button<{ $seated: boolean; $ring?: boolean }>`
	position: relative;
	width: 100%;
	height: 100%;
	min-height: 40px;
	border-radius: 10px;
	padding: 4px 8px;
	padding-right: 28px;
	text-align: left;
	cursor: pointer;
	border: none;
	border-left: 4px solid ${(p) => (p.$seated ? "#424242" : "#c62828")};
	background: ${(p) => (p.$seated ? "#424242" : "#f34a4a")};
	color: #fff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
	overflow: hidden;
	transition: transform 0.12s ease, box-shadow 0.12s ease;
	${(p) =>
		p.$ring ? `box-shadow: 0 0 0 3px #1a1a2e; transform: scale(1.02); z-index: 1;` : ""}
	&:hover {
		transform: scale(1.02);
		box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
	}
`;

const PartyBadge = styled.span`
	position: absolute;
	top: 4px;
	right: 6px;
	font-size: 9px;
	font-weight: 800;
	padding: 2px 6px;
	border-radius: 6px;
	background: rgba(255, 255, 255, 0.22);
	text-transform: uppercase;
`;

const QuickActionsBar = styled.div`
	position: absolute;
	top: -46px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 0;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 999px;
	padding: 4px;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
	z-index: 20;
	white-space: nowrap;
`;

const QuickActionPrimary = styled.button`
	background: #0f172a;
	color: #fff;
	border: none;
	border-radius: 999px;
	padding: 8px 14px;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	cursor: pointer;
	margin-right: 2px;
	&:hover {
		background: #000;
	}
`;

const QuickActionGhost = styled.button<{ $active?: boolean }>`
	background: ${(p) => (p.$active ? "#f1f5f9" : "transparent")};
	color: ${(p) => (p.$active ? "#0f172a" : "#64748b")};
	border: none;
	border-radius: 999px;
	padding: 8px 12px;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;

const NotesPopover = styled.div`
	position: absolute;
	top: -118px;
	left: 50%;
	transform: translateX(-50%);
	width: 260px;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 16px;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
	padding: 12px;
	z-index: 25;
`;

const PlacementSlot = styled.button<{
	$valid: boolean;
	$requested?: boolean;
	$suggested?: boolean;
}>`
	position: absolute;
	top: 0;
	bottom: 0;
	border: none;
	padding: 0;
	margin: 0;
	z-index: ${(p) => (p.$requested || p.$suggested ? 20 : 15)};
	cursor: ${(p) => (p.$valid ? "pointer" : "default")};
	background: ${(p) => {
		if (p.$requested) return "rgba(16, 185, 129, 0.16)";
		if (p.$suggested) return "rgba(99, 102, 241, 0.14)";
		return p.$valid ? "rgba(99, 102, 241, 0.06)" : "transparent";
	}};
	box-shadow: ${(p) => {
		if (p.$requested) return "inset 0 0 0 2px #10b981";
		if (p.$suggested) return "inset 0 0 0 2px #6366f1";
		return "none";
	}};
	pointer-events: ${(p) => (p.$valid ? "auto" : "none")};
	animation: ${(p) => (p.$suggested ? "cwPulsePlaced 1.8s ease-in-out infinite" : "none")};
	&:hover {
		background: ${(p) => {
			if (!p.$valid) return "transparent";
			if (p.$requested) return "rgba(16, 185, 129, 0.26)";
			if (p.$suggested) return "rgba(99, 102, 241, 0.22)";
			return "rgba(99, 102, 241, 0.16)";
		}};
	}
	@keyframes cwPulsePlaced {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.72;
		}
	}
`;

const QuickActionDocket = styled.button`
	background: #4f46e5;
	color: #fff;
	border: none;
	border-radius: 999px;
	padding: 8px 14px;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	cursor: pointer;
	margin-right: 2px;
	&:hover {
		background: #4338ca;
	}
`;

const DocketBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 200;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
	background: rgba(15, 23, 42, 0.72);
	backdrop-filter: blur(6px);
	@media print {
		background: transparent;
		padding: 0;
	}
`;

const DocketCard = styled.div`
	background: #fff;
	border-radius: 40px;
	box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
	max-width: 420px;
	width: 100%;
	padding: 2.25rem 2.5rem;
	text-align: center;
	@media print {
		box-shadow: none;
		border-radius: 0;
		max-width: none;
		padding: 1.5rem;
	}
`;

const DocketEyebrow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 0.5rem;
`;

const DocketEyebrowText = styled.span`
	font-size: 0.625rem;
	font-weight: 800;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.2em;
`;

const DocketCloseBtn = styled.button`
	border: none;
	background: transparent;
	color: #cbd5e1;
	font-size: 1.25rem;
	line-height: 1;
	cursor: pointer;
	padding: 0.25rem;
	border-radius: 8px;
	&:hover {
		color: #0f172a;
		background: #f1f5f9;
	}
`;

const DocketTableBand = styled.div`
	padding: 1.25rem 0 1.5rem;
	margin-bottom: 0.5rem;
	border-bottom: 2px dashed #e2e8f0;
	@media print {
		border-color: #cbd5e1;
	}
`;

const DocketTableTitle = styled.h3`
	margin: 0;
	font-size: 2.25rem;
	font-weight: 900;
	color: #0f172a;
	letter-spacing: -0.03em;
	line-height: 1.1;
`;

const DocketVenue = styled.p`
	margin: 0.35rem 0 0;
	font-size: 0.6875rem;
	font-weight: 700;
	color: #64748b;
	text-transform: uppercase;
	letter-spacing: 0.14em;
`;

const DocketGuestBlock = styled.div`
	padding: 1.5rem 0 1rem;
	text-align: center;
`;

const DocketGuestLabel = styled.p`
	margin: 0 0 0.35rem;
	font-size: 0.75rem;
	font-weight: 700;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.12em;
`;

const DocketGuestName = styled.h4`
	margin: 0;
	font-size: 1.5rem;
	font-weight: 900;
	color: #0f172a;
	letter-spacing: -0.02em;
`;

const DocketParty = styled.p`
	margin: 0.35rem 0 0;
	font-size: 1.0625rem;
	font-weight: 700;
	color: #334155;
`;

const DocketHintBox = styled.div`
	margin-top: 1rem;
	padding: 1.25rem;
	background: #f8fafc;
	border-radius: 1.75rem;
	border: 2px solid #f1f5f9;
	@media print {
		background: transparent;
		border-color: #cbd5e1;
	}
`;

const DocketHintText = styled.p`
	margin: 0;
	font-size: 0.6875rem;
	font-weight: 700;
	color: #64748b;
	line-height: 1.5;
`;

const DocketActions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.65rem;
	margin-top: 1.5rem;
	@media print {
		display: none;
	}
`;

const QueueLegendFooter = styled.div`
	padding: 1rem 1.25rem;
	background: #f8fafc;
	border-top: 1px solid #f1f5f9;
`;

const QueueHintBanner = styled.div`
	padding: 0.75rem 1rem;
	margin: 0 1rem 0.5rem;
	border-radius: 12px;
	background: rgba(99, 102, 241, 0.08);
	border: 1px solid rgba(99, 102, 241, 0.2);
	font-size: 0.6875rem;
	font-weight: 700;
	color: #4338ca;
	line-height: 1.35;
`;

const QueueEmptyState = styled.div`
	padding: 2.5rem 1.25rem;
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.75rem;
`;

const QueueEmptyIcon = styled.div`
	width: 3rem;
	height: 3rem;
	border-radius: 1rem;
	background: #f1f5f9;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.25rem;
	opacity: 0.5;
`;

const LegendRow = styled.p`
	margin: 0 0 0.5rem;
	font-size: 0.6875rem;
	font-weight: 700;
	color: #64748b;
	display: flex;
	align-items: center;
	gap: 0.45rem;
	&:last-child {
		margin-bottom: 0;
	}
`;

const LegendSwatch = styled.span<{ $color: string }>`
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 3px;
	background: ${(p) => p.$color};
	flex-shrink: 0;
`;

/** Time-change confirmation (cloudtable-style), CW chrome. */
const TimeAdjustBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 190;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
	background: rgba(15, 23, 42, 0.72);
	backdrop-filter: blur(6px);
	@media print {
		display: none;
	}
`;

const TimeAdjustCard = styled.div`
	background: #fff;
	border-radius: 28px;
	box-shadow: 0 25px 50px rgba(0, 0, 0, 0.18);
	max-width: 420px;
	width: 100%;
	padding: 2rem;
	border: 2px solid ${(p) => p.theme.colors?.border || "#e8e8e8"};
`;

const TimeAdjustIcon = styled.div`
	width: 4rem;
	height: 4rem;
	border-radius: 1.25rem;
	background: #fef3c7;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.75rem;
	margin-bottom: 1.25rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const BlockTitle = styled.p<{ $strike?: boolean }>`
	font-size: 11px;
	font-weight: 800;
	margin: 0;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	text-transform: uppercase;
	${(p) =>
		p.$strike ? `text-decoration: line-through; opacity: 0.55;` : ""}
`;

const BlockMeta = styled.p`
	font-size: 9px;
	font-weight: 700;
	margin: 2px 0 0;
	opacity: 0.9;
`;

function getTimelineBounds(
	r: T.Schema.Restaurant.RestaurantSchema,
	day: Moment,
	tz: string
): { startHour: number; endHour: number } {
	const weekday = day.clone().tz(tz).locale("en").format("dddd") as T.DaysOfWeek;
	const custom = r.settings.services?.table_booking?.hours;
	const useServiceHours =
		custom && Array.isArray(custom) && custom.length > 0;
	const slots = useServiceHours
		? custom!.filter((s) => s.day === weekday)
		: (r.location.opening_hours || []).filter((s) => s.day === weekday);
	if (!slots.length) {
		return { startHour: DEFAULT_TIMELINE_START, endHour: DEFAULT_TIMELINE_END };
	}
	let startMin = 24 * 60;
	let endMin = 0;
	for (const s of slots) {
		if (s.h24) {
			return { startHour: 0, endHour: 24 };
		}
		const open = s.open || "09:00";
		const close = s.close || "22:00";
		const [oh, om] = open.split(":").map((x) => parseInt(x, 10) || 0);
		const [ch, cm] = close.split(":").map((x) => parseInt(x, 10) || 0);
		const o = oh * 60 + om;
		let c = ch * 60 + cm;
		if (c <= o) c += 24 * 60;
		startMin = Math.min(startMin, o);
		endMin = Math.max(endMin, c);
	}
	const startHour = Math.max(0, Math.floor(startMin / 60));
	const endHour = Math.min(24, Math.ceil(endMin / 60));
	if (endHour <= startHour) {
		return { startHour: DEFAULT_TIMELINE_START, endHour: DEFAULT_TIMELINE_END };
	}
	return { startHour, endHour };
}

/** Matches store “Time interval (minutes)” for table booking (`times.conditions.time_interval`). */
function getTableBookingSlotIntervalMinutes(
	r: T.Schema.Restaurant.RestaurantSchema
): number {
	const raw =
		r.settings.services?.table_booking?.times?.conditions?.time_interval;
	const n = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
	if (!Number.isFinite(n) || n < 1) {
		return 15;
	}
	return Math.floor(n);
}

function assignLanes(
	items: T.Schema.Booking.BookingSchema[],
	startMinuteFn: (b: T.Schema.Booking.BookingSchema) => number,
	durationMinsFn: (b: T.Schema.Booking.BookingSchema) => number
): Record<string, number> {
	const intervals = items
		.map((b) => ({
			id: b._id,
			start: startMinuteFn(b),
			end: startMinuteFn(b) + durationMinsFn(b),
		}))
		.sort((a, b) => a.start - b.start);
	const laneEnds: number[] = [];
	const out: Record<string, number> = {};
	for (const iv of intervals) {
		let L = 0;
		while (L < laneEnds.length && laneEnds[L] > iv.start) L++;
		if (L === laneEnds.length) laneEnds.push(iv.end);
		else laneEnds[L] = iv.end;
		out[iv.id] = L;
	}
	return out;
}

function slotOverlapsBooking(
	b: T.Schema.Booking.BookingSchema,
	slotStartMins: number,
	slotEndMins: number,
	startHour: number,
	tz: string,
	durationMinsFn: (b: T.Schema.Booking.BookingSchema) => number
): boolean {
	const m = moment(b.config.timestamp).tz(tz);
	const bStart = m.hours() * 60 + m.minutes() - startHour * 60;
	const bEnd = bStart + durationMinsFn(b);
	return slotStartMins < bEnd && slotEndMins > bStart;
}

/** Cloudtable-style: slot is free on this table for the moving booking’s duration. */
function isTableSlotAvailable(
	tableId: string,
	slotStartMins: number,
	durationMins: number,
	items: T.Schema.Booking.BookingSchema[],
	ignoreBookingId: string | undefined,
	startHour: number,
	tz: string,
	durationMinsFn: (b: T.Schema.Booking.BookingSchema) => number
): boolean {
	const slotEndMins = slotStartMins + durationMins;
	for (const b of items) {
		if (b._id === ignoreBookingId) continue;
		if (!b.table_ids?.includes(tableId)) continue;
		if (b.status === "cancelled" || b.status === "complete") continue;
		if (
			slotOverlapsBooking(
				b,
				slotStartMins,
				slotEndMins,
				startHour,
				tz,
				durationMinsFn
			)
		) {
			return false;
		}
	}
	return true;
}

/** Matches cloudtable `placementDetails`: requested window vs next-free suggested window. */
function computePlacementHighlight(
	mb: T.Schema.Booking.BookingSchema,
	ctx: {
		startHour: number;
		totalMinutes: number;
		tz: string;
		tables: T.Schema.Restaurant.Services.DineInTable[];
		items: T.Schema.Booking.BookingSchema[];
		durationMinsFn: (b: T.Schema.Booking.BookingSchema) => number;
		slotIntervalMins: number;
	}
): { reqMins: number; dur: number; isOriginalSlotFree: boolean; suggestedMins: number } {
	const step = Math.max(1, ctx.slotIntervalMins);
	const m = moment(mb.config.timestamp).tz(ctx.tz);
	const reqMins = m.hours() * 60 + m.minutes() - ctx.startHour * 60;
	const dur = ctx.durationMinsFn(mb);
	const suitableTables = ctx.tables.filter(
		(tbl) => dineInTableCapacity(tbl, 4) >= mb.config.number_of_people
	);
	const isFreeAt = (tblId: string, startM: number) =>
		isTableSlotAvailable(
			tblId,
			startM,
			dur,
			ctx.items,
			mb._id,
			ctx.startHour,
			ctx.tz,
			ctx.durationMinsFn
		);
	const isOriginalSlotFree = suitableTables.some((tbl) => isFreeAt(tbl._id, reqMins));
	let suggestedMins = reqMins;
	if (!isOriginalSlotFree) {
		let offset = step;
		while (offset < ctx.totalMinutes) {
			if (suitableTables.some((tbl) => isFreeAt(tbl._id, reqMins + offset))) {
				suggestedMins = reqMins + offset;
				break;
			}
			if (suitableTables.some((tbl) => isFreeAt(tbl._id, reqMins - offset))) {
				suggestedMins = reqMins - offset;
				break;
			}
			offset += step;
		}
	}
	return { reqMins, dur, isOriginalSlotFree, suggestedMins };
}

interface Props extends WithTranslation {}

interface State {
	loading: boolean;
	items: T.Schema.Booking.BookingSchema[];
	day: Moment;
	selectedId: string | null;
	turnMinutes: number;
	editingNotesId: string | null;
	notesDraft: string;
	/** Cloudtable-style: pick a table timeline slot to place this booking. */
	movingBookingId: string | null;
	/** Pending-queue inline confirm panel for unconfirmed bookings. */
	queueConfirmId: string | null;
	docketBooking: T.Schema.Booking.BookingSchema | null;
	/** Placing at a different time than the booking’s requested time — confirm notify guest. */
	adjustmentPending: {
		bookingId: string;
		newTs: number;
		tableId: string;
	} | null;
}

/**
 * Floor-style day calendar inspired by cloudtable `CalendarView`: pending queue + scrollable timeline
 * with table rows from Dine In. Booking blocks are read-only; click opens the existing detail modal route.
 */
@inject("store")
@observer
class BookingsCalendarClass extends MobxComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			loading: true,
			items: [],
			day: moment(),
			selectedId: null,
			turnMinutes: DEFAULT_BLOCK_DURATION_MINS,
			editingNotesId: null,
			notesDraft: "",
			movingBookingId: null,
			queueConfirmId: null,
			docketBooking: null,
			adjustmentPending: null,
		};
	}

	componentDidMount() {
		const r = this.injected.store.restaurant!;
		const def = r.settings.services?.table_booking?.booking_logic?.default_turn_minutes;
		if (def && def > 0) {
			this.setState({ turnMinutes: def });
		}
		this.loadDay(this.state.day);
	}

	openBooking = (row: T.Schema.Booking.BookingSchema) => {
		const { store } = this.injected;
		const rid = store.restaurant!._id;
		store.router.push(`/restaurant/${rid}/bookings?_id=${row._id}`);
		store.notifications.mark_read_object("booking", row._id);
		this.setState({
			selectedId: row._id,
			movingBookingId: null,
			queueConfirmId: null,
			editingNotesId: null,
		});
	};

	/** Matches cloudtable `handleUnallocatedClick`: unconfirmed → inline confirm; else → placement mode. */
	handleQueueCardClick = (b: T.Schema.Booking.BookingSchema, ev: React.MouseEvent) => {
		ev.stopPropagation();
		const { queueConfirmId, movingBookingId } = this.state;
		if (b.status === "unconfirmed") {
			this.setState({
				queueConfirmId: queueConfirmId === b._id ? null : b._id,
				movingBookingId: null,
				selectedId: null,
				editingNotesId: null,
			});
		} else {
			this.setState({
				movingBookingId: movingBookingId === b._id ? null : b._id,
				queueConfirmId: null,
				selectedId: null,
				editingNotesId: null,
			});
		}
	};

	confirmQueueBooking = async (
		b: T.Schema.Booking.BookingSchema,
		opts: { silent: boolean },
		ev: React.MouseEvent
	) => {
		ev.stopPropagation();
		const { store } = this.injected;
		try {
			const res = await store.api.booking_update_status({
				_id: b._id,
				status: "confirmed",
				skip_customer_email: opts.silent,
			});
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "confirm_failed");
			}
			this.setState((s) => ({
				items: s.items.map((x) => (x._id === b._id ? res.booking : x)),
				queueConfirmId: null,
			}));
			UI.notification.success(
				opts.silent
					? "Booking confirmed (no email)"
					: "Booking confirmed"
			);
		} catch {
			UI.notification.error("Could not confirm booking");
		}
	};

	toggleBookingSelect = (row: T.Schema.Booking.BookingSchema, ev: React.MouseEvent) => {
		ev.stopPropagation();
		const { selectedId } = this.state;
		if (selectedId === row._id) {
			this.setState({
				selectedId: null,
				movingBookingId: null,
				queueConfirmId: null,
				editingNotesId: null,
				notesDraft: "",
			});
		} else {
			this.setState({
				selectedId: row._id,
				movingBookingId: null,
				queueConfirmId: null,
				editingNotesId: null,
				notesDraft: row.notes || "",
			});
		}
	};

	durationMinsForBooking = (
		b: T.Schema.Booking.BookingSchema,
		fallbackTurn: number
	) =>
		b.table_turn_minutes && b.table_turn_minutes > 0 ? b.table_turn_minutes : fallbackTurn;

	scheduleNotesSave = (bookingId: string, text: string) => {
		const { store } = this.injected;
		const id = `notes-save-${bookingId}`;
		const w = window as unknown as { __cwNotesTimers?: Record<string, number> };
		if (!w.__cwNotesTimers) w.__cwNotesTimers = {};
		if (w.__cwNotesTimers[id]) {
			window.clearTimeout(w.__cwNotesTimers[id]);
		}
		w.__cwNotesTimers[id] = window.setTimeout(async () => {
			try {
				const res = await store.api.booking_update_fields({
					_id: bookingId,
					notes: text,
				});
				if (res.outcome !== 0) {
					throw new Error((res as { message?: string }).message || "save_failed");
				}
				this.setState((s) => ({
					items: s.items.map((x) => (x._id === bookingId ? res.booking : x)),
				}));
			} catch {
				UI.notification.error("Could not save notes");
			}
		}, 450);
	};

	applyPlacement = async (
		tableId: string,
		slotMinsRaw: number,
		ctx: {
			day: Moment;
			tz: string;
			startHour: number;
			items: T.Schema.Booking.BookingSchema[];
			tables: T.Schema.Restaurant.Services.DineInTable[];
		}
	) => {
		const { movingBookingId, turnMinutes } = this.state;
		const b = ctx.items.find((x) => x._id === movingBookingId);
		if (!b) return;
		const cap = dineInTableCapacity(
			ctx.tables.find((t) => t._id === tableId) || { seats: 4 },
			4
		);
		if (cap < b.config.number_of_people) return;
		const slotIv = getTableBookingSlotIntervalMinutes(this.injected.store.restaurant!);
		const snapped = Math.round(slotMinsRaw / slotIv) * slotIv;
		const dur = this.durationMinsForBooking(b, turnMinutes);
		if (
			!isTableSlotAvailable(
				tableId,
				snapped,
				dur,
				ctx.items,
				b._id,
				ctx.startHour,
				ctx.tz,
				(x) => this.durationMinsForBooking(x, turnMinutes)
			)
		) {
			return;
		}
		const newTs = ctx.day
			.clone()
			.tz(ctx.tz)
			.startOf("day")
			.add(ctx.startHour * 60 + snapped, "minutes")
			.valueOf();

		/** Same threshold as cloudtable `isTimeChanged` (> 1 minute). */
		if (Math.abs(newTs - b.config.timestamp) > 60_000) {
			this.setState({
				adjustmentPending: {
					bookingId: b._id,
					newTs,
					tableId,
				},
				movingBookingId: null,
			});
			return;
		}

		await this.commitPlacementFields(b._id, tableId, newTs, b.config.timestamp, {
			notifyGuest: false,
		});
	};

	commitPlacementFields = async (
		bookingId: string,
		tableId: string,
		newTs: number,
		originalTs: number,
		opts: { notifyGuest: boolean }
	) => {
		const { store } = this.injected;
		const payload: T.API.DashboardBookingUpdateFieldsRequest = {
			_id: bookingId,
			table_ids: [tableId],
		};
		if (Math.abs(newTs - originalTs) >= 60_000) {
			payload.config_timestamp = newTs;
			payload.skip_customer_email = !opts.notifyGuest;
		}
		try {
			const res = await store.api.booking_update_fields(payload);
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "move_failed");
			}
			this.setState((s) => ({
				items: s.items.map((x) => (x._id === bookingId ? res.booking : x)),
				movingBookingId: null,
				selectedId: null,
				adjustmentPending: null,
			}));
			UI.notification.success("Booking placed");
		} catch {
			UI.notification.error("Could not move booking");
		}
	};

	finalizeTimeAdjustment = async (notifyGuest: boolean) => {
		const p = this.state.adjustmentPending;
		if (!p) return;
		const b = this.state.items.find((x) => x._id === p.bookingId);
		if (!b) {
			this.setState({ adjustmentPending: null });
			return;
		}
		await this.commitPlacementFields(p.bookingId, p.tableId, p.newTs, b.config.timestamp, {
			notifyGuest,
		});
	};

	cancelTimeAdjustment = () => {
		const id = this.state.adjustmentPending?.bookingId;
		this.setState({
			adjustmentPending: null,
			movingBookingId: id ?? null,
		});
	};

	seatGuest = async (b: T.Schema.Booking.BookingSchema, e: React.MouseEvent) => {
		e.stopPropagation();
		const { store } = this.injected;
		try {
			const res = await store.api.booking_update_status({
				_id: b._id,
				status: "seated",
			});
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "seat_failed");
			}
			this.setState((s) => ({
				items: s.items.map((x) => (x._id === b._id ? res.booking : x)),
				selectedId: null,
				editingNotesId: null,
				docketBooking: res.booking,
			}));
		} catch {
			UI.notification.error("Could not update seating");
		}
	};

	closeDocket = () => this.setState({ docketBooking: null });

	printDocket = () => window.print();

	renderBookingBlock(
		b: T.Schema.Booking.BookingSchema,
		ctx: {
			left: number;
			width: number;
			top: number;
			laneHeight: number;
			items: T.Schema.Booking.BookingSchema[];
			tables: T.Schema.Restaurant.Services.DineInTable[];
			t: WithTranslation["t"];
		},
		reactKey: string,
		showQuickActions: boolean
	) {
		const { selectedId, editingNotesId, notesDraft, movingBookingId, turnMinutes } =
			this.state;
		const isSeated = b.status === "seated";
		const isComplete = b.status === "complete";
		const seatedStyle = isSeated || isComplete;
		const canSeatGuest = b.status === "confirmed";
		const canMovePlacement =
			b.status === "confirmed" || b.status === "unconfirmed";
		const selected = selectedId === b._id;
		const z = selected ? 8 : 2;
		const dim = !!movingBookingId && movingBookingId !== b._id;
		const pe = movingBookingId ? "none" : "auto";

		return (
			<BookingBlockOuter
				key={reactKey}
				$left={ctx.left}
				$width={ctx.width}
				$top={ctx.top}
				$laneHeight={ctx.laneHeight}
				$z={z}
				$pe={pe}
				style={
					dim
						? { opacity: 0.12, transform: "scale(0.96)" }
						: undefined
				}
			>
				{selected && !movingBookingId && showQuickActions && (
					<QuickActionsBar onClick={(e) => e.stopPropagation()}>
						{canSeatGuest ? (
							<QuickActionPrimary
								type="button"
								onClick={(e) => this.seatGuest(b, e)}
							>
								{ctx.t("Seat Guest", { defaultValue: "Seat Guest" })}
							</QuickActionPrimary>
						) : isSeated || isComplete ? (
							<QuickActionDocket
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									this.setState({
										docketBooking: b,
										selectedId: null,
									});
								}}
							>
								{ctx.t("Docket", { defaultValue: "Docket" })}
							</QuickActionDocket>
						) : null}
						{canMovePlacement && !seatedStyle && (
							<QuickActionGhost
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									this.setState({
										movingBookingId: b._id,
										selectedId: null,
										queueConfirmId: null,
										editingNotesId: null,
									});
								}}
							>
								{ctx.t("Move", { defaultValue: "Move" })}
							</QuickActionGhost>
						)}
						<QuickActionGhost
							type="button"
							$active={editingNotesId === b._id}
							onClick={(e) => {
								e.stopPropagation();
								this.setState((s) => {
									const closing = s.editingNotesId === b._id;
									return {
										editingNotesId: closing ? null : b._id,
										notesDraft: closing ? "" : b.notes || "",
									};
								});
							}}
						>
							{ctx.t("Notes", { defaultValue: "Notes" })}
							{b.notes?.trim() ? " •" : ""}
						</QuickActionGhost>
						{editingNotesId === b._id && (
							<NotesPopover onClick={(e) => e.stopPropagation()}>
								<textarea
									className="form-control"
									style={{
										width: "100%",
										minHeight: 72,
										fontSize: 12,
										borderRadius: 10,
									}}
									value={notesDraft}
									placeholder={ctx.t("Guest notes", {
										defaultValue: "Guest notes",
									})}
									onChange={(e) => {
										const v = e.target.value;
										this.setState({ notesDraft: v });
										this.scheduleNotesSave(b._id, v);
									}}
								/>
							</NotesPopover>
						)}
					</QuickActionsBar>
				)}
				<BookingBlockBtn
					type="button"
					$seated={seatedStyle}
					$ring={selected}
					onClick={(e) => this.toggleBookingSelect(b, e)}
				>
					<BlockTitle $strike={isSeated}>
						{isSeated && <span style={{ marginRight: 4 }}>{"\u2713 "}</span>}
						{b.customer.name}
					</BlockTitle>
					<BlockMeta>
						{ctx.t("datetimeFromTimestamp", {
							value: b.config.timestamp,
						})}
					</BlockMeta>
					<PartyBadge>
						{ctx.t("number", { value: b.config.number_of_people })}P
					</PartyBadge>
				</BookingBlockBtn>
			</BookingBlockOuter>
		);
	}

	loadDay = async (day: Moment) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const start = day.clone().tz(tz).startOf("day").valueOf();
		const end = day.clone().tz(tz).endOf("day").valueOf();
		this.setState({
			loading: true,
			day,
			movingBookingId: null,
			queueConfirmId: null,
			adjustmentPending: null,
		});
		try {
			const response = await store.api.bookings_find({
				page: 1,
				limit: 500,
				sort: { "config.timestamp": 1 },
				query: {
					restaurant_id: r._id,
					"config.timestamp": { $gte: start, $lte: end },
				},
			});
			if (response.outcome) {
				throw new Error(response.message);
			}
			this.setState({ items: response.items, loading: false });
		} catch (e) {
			this.setState({ items: [], loading: false });
		}
	};

	render() {
		const { store, t } = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const {
			loading,
			items,
			day,
			selectedId,
			turnMinutes,
			movingBookingId,
			queueConfirmId,
			docketBooking,
			adjustmentPending,
		} = this.state;
		const narrow = store.view.screen_width < 768;
		const slotSize = narrow ? 80 : 120;
		const tableColW = narrow
			? TABLE_COL_WIDTH_NARROW_PX
			: TABLE_COL_WIDTH_WIDE_PX;

		const { startHour, endHour } = getTimelineBounds(r, day, tz);
		const numHours = Math.max(1, endHour - startHour);
		const timelineInnerWidth = numHours * slotSize;
		const minuteSize = slotSize / 60;
		const totalMinutes = numHours * 60;
		const slotIntervalMins = getTableBookingSlotIntervalMinutes(r);
		const minorGrid = slotIntervalMins * minuteSize;

		const label = day.clone().tz(tz).locale(store.intl.s.lng).format("dddd, D MMM YYYY");
		const isToday = day.clone().tz(tz).isSame(moment().tz(tz), "day");

		let nowPos: number | null = null;
		if (isToday) {
			const now = moment().tz(tz);
			const minsSinceStart = now.hours() * 60 + now.minutes() - startHour * 60;
			if (minsSinceStart >= 0 && minsSinceStart <= totalMinutes) {
				nowPos = tableColW + minsSinceStart * minuteSize;
			}
		}

		const timeLabels: string[] = [];
		for (let h = startHour; h < endHour; h++) {
			const hour = ((h % 24) + 24) % 24;
			timeLabels.push(
				day
					.clone()
					.tz(tz)
					.hour(hour)
					.minute(0)
					.second(0)
					.locale(store.intl.s.lng)
					.format("LT")
			);
		}

		/** Same filter as cloudtable unallocated: no table or still unconfirmed. */
		const queueItems = items.filter(
			(b) =>
				b.status !== "cancelled" &&
				b.status !== "complete" &&
				(!b.table_ids?.length || b.status === "unconfirmed")
		);

		const startMinuteFromBooking = (b: T.Schema.Booking.BookingSchema) => {
			const m = moment(b.config.timestamp).tz(tz);
			return m.hours() * 60 + m.minutes() - startHour * 60;
		};

		const durationMinsFn = (b: T.Schema.Booking.BookingSchema) =>
			this.durationMinsForBooking(b, turnMinutes);
		const laneHeight = BOOKING_LANE_HEIGHT_PX;
		const mbPlace = movingBookingId
			? items.find((x) => x._id === movingBookingId)
			: null;

		const tables: T.Schema.Restaurant.Services.DineInTable[] =
			r.settings.services?.dine_in?.tables || [];

		const placementDetails =
			movingBookingId && mbPlace
				? computePlacementHighlight(mbPlace, {
						startHour,
						totalMinutes,
						tz,
						tables,
						items,
						durationMinsFn,
						slotIntervalMins,
					})
				: null;

		return (
			<div>
				<CalendarPageHeader className="m-b-3">
					<div>
						<CalendarTitle>
							{t("Bookings", { defaultValue: "Bookings" })}
						</CalendarTitle>
						<CalendarSubtitle>{label}</CalendarSubtitle>
					</div>
					<CalendarToolbar>
						<ToolbarPill>
							<ToolbarIconBtn
								type="button"
								aria-label={t("Previous day", { defaultValue: "Previous day" })}
								onClick={() => this.loadDay(day.clone().subtract(1, "day"))}
							>
								<FaChevronLeft />
							</ToolbarIconBtn>
							<ToolbarTodayBtn
								type="button"
								onClick={() => this.loadDay(moment().tz(tz))}
							>
								{t("Today", { defaultValue: "Today" })}
							</ToolbarTodayBtn>
							<ToolbarIconBtn
								type="button"
								aria-label={t("Next day", { defaultValue: "Next day" })}
								onClick={() => this.loadDay(day.clone().add(1, "day"))}
							>
								<FaChevronRight />
							</ToolbarIconBtn>
						</ToolbarPill>
						<TurnPill>
							<TurnPillLabel>
								{t("Turn", { defaultValue: "Turn" })}
							</TurnPillLabel>
							<TurnPillInput
								type="number"
								min={15}
								step={15}
								aria-label={t("Turn minutes", { defaultValue: "Turn minutes" })}
								value={turnMinutes}
								onChange={(e) =>
									this.setState({
										turnMinutes: Math.max(
											15,
											parseInt(e.target.value, 10) || 15
										),
									})
								}
							/>
							<span className="tiny font-bold text-muted">min</span>
						</TurnPill>
					</CalendarToolbar>
				</CalendarPageHeader>

				{loading ? (
					<p className="small text-muted">Loading…</p>
				) : (
					<CalendarShell>
						<QueuePanel>
							<QueueHeader
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 8,
								}}
							>
								<span>
									{t("Pending queue", { defaultValue: "Pending queue" })} (
									{queueItems.length})
								</span>
								{movingBookingId && (
									<button
										type="button"
										title={t("Cancel move", { defaultValue: "Cancel move" })}
										onClick={() =>
											this.setState({
												movingBookingId: null,
												queueConfirmId: null,
											})
										}
										style={{
											width: 28,
											height: 28,
											borderRadius: 999,
											border: "none",
											background: "#6366f1",
											color: "#fff",
											fontSize: 11,
											fontWeight: 800,
											cursor: "pointer",
											flexShrink: 0,
											lineHeight: 1,
										}}
									>
										{"\u00D7"}
									</button>
								)}
							</QueueHeader>
							{movingBookingId && (
								<QueueHintBanner>
									{t("Click an open slot on a table row to place this booking.", {
										defaultValue:
											"Click an open slot on a table row to place this booking.",
									})}
								</QueueHintBanner>
							)}
							<QueueList>
								{queueItems.length === 0 ? (
									<QueueEmptyState>
										<QueueEmptyIcon aria-hidden>{"\u2705"}</QueueEmptyIcon>
										<p className="small lhp text-muted m-b-0">
											{t("Queue clear. All bookings allocated.", {
												defaultValue: "Queue clear. All bookings allocated.",
											})}
										</p>
									</QueueEmptyState>
								) : (
									queueItems.map((b) => {
										const isUnconfirmed = b.status === "unconfirmed";
										const isConfirming = queueConfirmId === b._id;
										const isActive =
											isConfirming ||
											movingBookingId === b._id;
										return (
											<QueueCardRoot
												key={b._id}
												$active={isActive}
												$unconfirmed={isUnconfirmed}
												role="button"
												tabIndex={0}
												onClick={(e) => this.handleQueueCardClick(b, e)}
												onKeyDown={(e) => {
													if (e.key === "Enter" || e.key === " ") {
														e.preventDefault();
														this.handleQueueCardClick(
															b,
															e as unknown as React.MouseEvent
														);
													}
												}}
											>
												<QueueCardTop>
													<div style={{ minWidth: 0 }}>
														<QueueGuest $unconfirmed={isUnconfirmed}>
															{b.customer.name}
														</QueueGuest>
														<div>
															<QueueTime>
																{t("datetimeFromTimestamp", {
																	value: b.config.timestamp,
																})}
															</QueueTime>
														</div>
													</div>
													<QueueParty>
														{t("number", { value: b.config.number_of_people })}P
													</QueueParty>
												</QueueCardTop>

												{isConfirming ? (
													<QueueConfirmActions
														onClick={(e) => e.stopPropagation()}
													>
														<QueueConfirmBtn
															type="button"
															$primary
															onClick={(e) =>
																this.confirmQueueBooking(b, { silent: false }, e)
															}
														>
															{t("Confirm Email", { defaultValue: "Confirm Email" })}
														</QueueConfirmBtn>
														<QueueConfirmBtn
															type="button"
															onClick={(e) =>
																this.confirmQueueBooking(b, { silent: true }, e)
															}
														>
															{t("Confirm Silent", { defaultValue: "Confirm Silent" })}
														</QueueConfirmBtn>
													</QueueConfirmActions>
												) : (
													<QueueFooter>
														{isUnconfirmed ? (
															<QueueBadge $variant="assign">
																{t("Assign Table", { defaultValue: "Assign Table" })}
															</QueueBadge>
														) : (
															<QueueBadge $variant="ready">
																{t("Ready", { defaultValue: "Ready" })}
															</QueueBadge>
														)}
														<QueuePlaceHint>
															{t("Place →", { defaultValue: "Place →" })}
														</QueuePlaceHint>
													</QueueFooter>
												)}
											</QueueCardRoot>
										);
									})
								)}
							</QueueList>
							<QueueLegendFooter>
								<LegendRow>
									<LegendSwatch $color="#f34a4a" />
									{t("Active booking", { defaultValue: "Active / confirmed" })}
								</LegendRow>
								<LegendRow>
									<LegendSwatch $color="#424242" />
									{t("Seated / complete", { defaultValue: "Seated / complete" })}
								</LegendRow>
							</QueueLegendFooter>
						</QueuePanel>

						<GridWrap $placing={!!movingBookingId}>
							<ScrollArea
								style={{
									cursor: movingBookingId ? "crosshair" : undefined,
								}}
								onClick={() =>
									this.setState({
										selectedId: null,
										movingBookingId: null,
										queueConfirmId: null,
										editingNotesId: null,
									})
								}
							>
								<div style={{ minWidth: tableColW + timelineInnerWidth }}>
									{items.length === 0 && (
										<div
											className="p-3 text-center small text-muted"
											style={{
												borderBottom: "1px solid #eee",
												background: "#fafafa",
											}}
										>
											{t("No bookings for this day.", {
												defaultValue: "No bookings for this day.",
											})}
										</div>
									)}
									<StickyHeader>
											<CornerCell>{t("Table", { defaultValue: "Table" })}</CornerCell>
											<HourTrack>
												{timeLabels.map((lbl, i) => (
													<HourCell key={i} $w={slotSize}>
														{lbl}
													</HourCell>
												))}
											</HourTrack>
										</StickyHeader>

										<div style={{ position: "relative" }}>
											{nowPos !== null && <NowLine $left={nowPos} />}

											{tables.map((tbl, idx) => {
												const cap = tbl.seats && tbl.seats > 0 ? tbl.seats : 4;
												const title = tbl.name?.trim() || `T${idx + 1}`;
												const isTableSuitable = mbPlace
													? dineInTableCapacity(tbl, 4) >=
														mbPlace.config.number_of_people
													: true;
												const rowItems = items.filter((b) =>
													b.table_ids?.includes(tbl._id)
												);
												const lanesRow = assignLanes(
													rowItems,
													startMinuteFromBooking,
													durationMinsFn
												);
												const maxLR = rowItems.length
													? Math.max(...rowItems.map((b) => lanesRow[b._id] ?? 0))
													: 0;
												const rowH = Math.max(
													MIN_TABLE_ROW_PX,
													(maxLR + 1) * laneHeight + 12
												);
												const dimRow = !!movingBookingId && !!mbPlace && !isTableSuitable;
												return (
													<GridRow
														key={tbl._id || idx}
														style={{
															opacity: dimRow ? 0.22 : 1,
															filter: dimRow ? "grayscale(0.9)" : undefined,
															pointerEvents: dimRow ? "none" : undefined,
															transition: "opacity 0.2s ease",
														}}
													>
														<RowLabel
															style={
																movingBookingId && mbPlace && isTableSuitable
																	? {
																			background: "rgba(238, 242, 255, 0.98)",
																			borderRightColor: "#c7d2fe",
																		}
																	: undefined
															}
														>
															<RowLabelTitle>{title}</RowLabelTitle>
															<RowLabelMeta>
																{t("number", { value: cap })}{" "}
																{t("seats", { defaultValue: "seats" })}
															</RowLabelMeta>
														</RowLabel>
														<TimelineTrack
															$w={timelineInnerWidth}
															$slot={slotSize}
															$minor={minorGrid}
															style={{ height: rowH, opacity: 0.92 }}
														>
															{rowItems.map((b) => {
																const startM = startMinuteFromBooking(b);
																const left = Math.max(0, startM * minuteSize);
																const width =
																	this.durationMinsForBooking(b, turnMinutes) *
																	minuteSize;
																const lane = lanesRow[b._id] ?? 0;
																const top = 4 + lane * laneHeight;
																return this.renderBookingBlock(
																	b,
																	{
																		left,
																		width,
																		top,
																		laneHeight,
																		items,
																		tables,
																		t,
																	},
																	`${b._id}-${tbl._id}`,
																	tbl._id === b.table_ids?.[0]
																);
															})}
															{movingBookingId &&
																(() => {
																	const mb = items.find(
																		(x) => x._id === movingBookingId
																	);
																	if (!mb) return null;
																	const dur = this.durationMinsForBooking(
																		mb,
																		turnMinutes
																	);
																	const capOk =
																		dineInTableCapacity(tbl, 4) >=
																		mb.config.number_of_people;
																	const numSlots = Math.max(
																		1,
																		Math.floor(
																			totalMinutes / slotIntervalMins
																		)
																	);
																	return Array.from(
																		{ length: numSlots },
																		(_, i) => {
																			const slotMins = i * slotIntervalMins;
																			const valid =
																				capOk &&
																				isTableSlotAvailable(
																					tbl._id,
																					slotMins,
																					dur,
																					items,
																					mb._id,
																					startHour,
																					tz,
																					durationMinsFn);
																			if (!valid) {
																				return null;
																			}
																			const isRequested =
																				!!placementDetails &&
																				slotMins >=
																					placementDetails.reqMins &&
																				slotMins <
																					placementDetails.reqMins +
																						placementDetails.dur;
																			const isSuggested =
																				!!placementDetails &&
																				!placementDetails.isOriginalSlotFree &&
																				slotMins >=
																					placementDetails.suggestedMins &&
																				slotMins <
																					placementDetails.suggestedMins +
																						placementDetails.dur;
																			return (
																				<PlacementSlot
																					key={`pl-${tbl._id}-${i}`}
																					type="button"
																					aria-label={t(
																						"Place booking",
																						{
																							defaultValue:
																								"Place booking",
																						}
																					)}
																					$valid={valid}
																					$requested={isRequested}
																					$suggested={
																						isSuggested && !isRequested
																					}
																					style={{
																						left:
																							slotMins * minuteSize,
																						width:
																							slotIntervalMins *
																							minuteSize,
																					}}
																					onClick={(e) => {
																						e.stopPropagation();
																						this.applyPlacement(
																							tbl._id,
																							slotMins,
																							{
																								day,
																								tz,
																								startHour,
																								items,
																								tables,
																							}
																						);
																					}}
																				/>
																			);
																		}
																	);
																})()}
														</TimelineTrack>
													</GridRow>
												);
											})}

											{tables.length === 0 && (
												<GridRow>
													<RowLabel>
														<RowLabelTitle>—</RowLabelTitle>
														<RowLabelMeta>
															{t("Add tables in Dine In settings", {
																defaultValue: "Add tables in Dine In",
															})}
														</RowLabelMeta>
													</RowLabel>
													<TimelineTrack
														$w={timelineInnerWidth}
														$slot={slotSize}
														$minor={minorGrid}
														style={{ minHeight: MIN_TABLE_ROW_PX }}
													/>
												</GridRow>
											)}
										</div>
									</div>
								</ScrollArea>
						</GridWrap>
					</CalendarShell>
				)}
				{adjustmentPending &&
					(() => {
						const adjBooking = items.find(
							(x) => x._id === adjustmentPending.bookingId
						);
						if (!adjBooking) return null;
						return (
							<TimeAdjustBackdrop
								onClick={(e) => {
									if (e.target === e.currentTarget) {
										this.cancelTimeAdjustment();
									}
								}}
							>
								<TimeAdjustCard onClick={(e) => e.stopPropagation()}>
									<TimeAdjustIcon aria-hidden>{"\u26A0\uFE0F"}</TimeAdjustIcon>
									<h3 className="font-bold m-b-2" style={{ fontSize: "1.35rem" }}>
										{t("Time adjustment", { defaultValue: "Time adjustment" })}
									</h3>
									<p className="small lhp text-muted m-b-4">
										{t("calendar.timeAdjust.body", {
											defaultValue:
												"Moving {{guest}} to {{when}}. Notify the guest?",
											guest: adjBooking.customer.name,
											when: t("datetimeFromTimestamp", {
												value: adjustmentPending.newTs,
											}),
										})}
									</p>
									<div className="flex-col gap-2" style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
										<Button
											type="button"
											color="primary"
											full
											onClick={() => this.finalizeTimeAdjustment(true)}
										>
											{t("Send email notification", {
												defaultValue: "Send email notification",
											})}
										</Button>
										<Button
											type="button"
											color="primary-inverse"
											full
											onClick={() => this.finalizeTimeAdjustment(false)}
										>
											{t("Confirm without notification", {
												defaultValue: "Confirm without notification",
											})}
										</Button>
										<Button
											type="button"
											color="white"
											full
											onClick={this.cancelTimeAdjustment}
										>
											{t("Cancel and revert", {
												defaultValue: "Cancel & revert",
											})}
										</Button>
									</div>
								</TimeAdjustCard>
							</TimeAdjustBackdrop>
						);
					})()}
				{docketBooking && (
					<DocketBackdrop
						className="print:block"
						onClick={(e) => {
							if (e.target === e.currentTarget) this.closeDocket();
						}}
					>
						<DocketCard onClick={(e) => e.stopPropagation()}>
							<DocketEyebrow className="print:hidden">
								<DocketEyebrowText>
									{t("Live table docket", { defaultValue: "Live table docket" })}
								</DocketEyebrowText>
								<DocketCloseBtn
									type="button"
									aria-label={t("Close", { defaultValue: "Close" })}
									onClick={this.closeDocket}
								>
									{"\u00D7"}
								</DocketCloseBtn>
							</DocketEyebrow>
							<DocketTableBand>
								<DocketTableTitle>
									{docketBooking.table_ids?.length
										? (() => {
												const tid = docketBooking.table_ids![0];
												const tt = tables.find((x) => x._id === tid);
												const label =
													tt?.name?.trim() ||
													t("Table", { defaultValue: "Table" });
												return `${t("TABLE", { defaultValue: "TABLE" })} ${label}`;
											})()
										: t("Unassigned", { defaultValue: "Unassigned" })}
								</DocketTableTitle>
								<DocketVenue>{r.name}</DocketVenue>
							</DocketTableBand>
							<DocketGuestBlock>
								<DocketGuestLabel>
									{t("Guest", { defaultValue: "Guest" })}
								</DocketGuestLabel>
								<DocketGuestName>{docketBooking.customer.name}</DocketGuestName>
								<DocketParty>
									{t("number", { value: docketBooking.config.number_of_people })}{" "}
									{t("guests", { defaultValue: "guests" })}
								</DocketParty>
							</DocketGuestBlock>
							<DocketHintBox>
								<DocketHintText>
									{t("datetimeFromTimestamp", {
										value: docketBooking.config.timestamp,
									})}
								</DocketHintText>
							</DocketHintBox>
							<DocketActions>
								<Button
									type="button"
									color="primary-inverse"
									full
									onClick={this.printDocket}
									style={{
										borderRadius: "1.5rem",
										padding: "0.85rem",
										fontSize: "0.6875rem",
										fontWeight: 800,
										textTransform: "uppercase",
										letterSpacing: "0.12em",
									}}
								>
									{t("Print docket", { defaultValue: "Print docket" })}
								</Button>
								<Button
									type="button"
									color="white"
									full
									onClick={this.closeDocket}
									style={{
										borderRadius: "1.5rem",
										padding: "0.85rem",
										fontSize: "0.6875rem",
										fontWeight: 800,
										textTransform: "uppercase",
										letterSpacing: "0.12em",
										color: "#94a3b8",
										background: "#f8fafc",
										border: "1px solid #f1f5f9",
									}}
								>
									{t("Done", { defaultValue: "Done" })}
								</Button>
							</DocketActions>
						</DocketCard>
					</DocketBackdrop>
				)}
			</div>
		);
	}
}

export const BookingsCalendar = withTranslation()(BookingsCalendarClass);
